// QuestionNumero - Pregunta tipo Número
// Sprint 3 - Mobile-first design

import React, { useState } from 'react';
import { Box, TextField, Chip, Typography } from '@mui/material';
import { PreguntaCompleta } from '../../../../types/formulario.types';
import { QuestionResponse } from '../../../../types/audit.types';

interface QuestionNumeroProps {
  pregunta: PreguntaCompleta;
  respuesta?: QuestionResponse;
  onChange: (valor: any, comentario?: string) => void;
}

const QuestionNumero: React.FC<QuestionNumeroProps> = ({
  pregunta,
  respuesta,
  onChange,
}) => {
  const valorActual = (respuesta as any)?.response_value as { valor?: number } | undefined;
  const [comentario, setComentario] = useState((respuesta as any)?.comment || '');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const valor = parseFloat(event.target.value);
    if (!isNaN(valor)) {
      onChange({ valor }, comentario || undefined);
    } else if (event.target.value === '') {
      onChange({ valor: 0 }, comentario || undefined);
    }
  };

  const handleComentarioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nuevoComentario = event.target.value;
    setComentario(nuevoComentario);
    if (valorActual?.valor !== undefined) {
      onChange({ valor: valorActual.valor }, nuevoComentario || undefined);
    }
  };

  const min = pregunta.config_validacion?.min;
  const max = pregunta.config_validacion?.max;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box sx={{ fontSize: { xs: '1rem', sm: '1.0625rem' }, fontWeight: 600 }}>
            {(pregunta as any).text}
          </Box>
          {(pregunta as any).required && (
            <Chip label="Obligatoria" size="small" color="error" sx={{ height: 20, fontSize: '0.6875rem' }} />
          )}
        </Box>
        {(pregunta as any).description && (
          <Box sx={{ fontSize: '0.875rem', color: 'text.secondary', fontStyle: 'italic', mt: 0.5 }}>
            {(pregunta as any).description}
          </Box>
        )}
      </Box>

      {/* Sección de respuesta */}
      <Box 
        sx={{ 
          p: 2, 
          bgcolor: 'action.hover', 
          borderRadius: 1,
          mb: pregunta.permite_comentario ? 2 : 0,
        }}
      >
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block', 
            mb: 1.5, 
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          Ingresa un Valor
        </Typography>
        {/* Campo numérico */}
        <TextField
          fullWidth
          type="number"
          value={valorActual?.valor ?? ''}
          onChange={handleChange}
          placeholder="Ingresa un número"
          variant="outlined"
          name={`pregunta-${pregunta.id}`}
          id={`pregunta-numero-${pregunta.id}`}
          inputProps={{
            min,
            max,
            step: 'any',
            inputMode: 'decimal', // Teclado numérico en mobile
          }}
          helperText={
            min !== undefined && max !== undefined
              ? `Valor entre ${min} y ${max}`
              : min !== undefined
              ? `Valor mínimo: ${min}`
              : max !== undefined
            ? `Valor máximo: ${max}`
            : undefined
          }
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.paper',
              fontSize: { xs: '1rem', sm: '0.9375rem' },
            },
            '& input': {
              fontSize: { xs: '1.125rem', sm: '1rem' },
              fontWeight: 500,
              py: { xs: 1.5, sm: 1 },
            },
          }}
        />
      </Box>

      {/* Comentario */}
      {pregunta.permite_comentario && (
        <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block', 
              mb: 1, 
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Comentario Adicional
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Comentario (opcional)"
            value={comentario}
            onChange={handleComentarioChange}
            placeholder="Agrega un comentario..."
            variant="outlined"
            size="small"
            name={`comentario-${pregunta.id}`}
            id={`comentario-numero-${pregunta.id}`}
            inputProps={{
              name: `comentario-${pregunta.id}`,
            }}
            InputProps={{
              name: `comentario-${pregunta.id}`,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default QuestionNumero;
